import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import PrintConfiguration, {
    Font,
    Margin,
    PrintTarget,
} from "../../types/system/PrintConfiguration";
import store, { IAppState } from "../Store";

type DisplayMode = "light" | "dark" | "auto";

// Define the type for our state store entry
type UIConfigurationState = {
    sidebarWidth: number;
    sidebarOpen: boolean;
    sidebarPinned: boolean;
    displayMenuOpen: boolean;
    shareMenuOpen: boolean;
    printMenuOpen: boolean;
    messagingOpen: boolean;
    printSupplementary: boolean;
    promptLandscape: boolean;
    promptAll: boolean;
    wordAsText: boolean;
    printConfig: PrintConfiguration;
    customPrintTitle?: string;
    customPrintName?: string;
    customPrintElement?: string;
    currentFile?: number;
    displayMode: DisplayMode;
    disableDOC: boolean;
    disableXLS: boolean;
    disablePDF: boolean;
    disableShare: boolean;
    reportName: string;
    reportsSidebar: boolean;
    pageLoading: boolean;
    pageLoadingOverlay: boolean;
    expandedMenuItems: string[];
    customPrintMethod?: (
        target: PrintTarget,
        method: "Table" | "Text",
    ) => Promise<any>;
};

// Define the initial state for our state store entry
const initialState: UIConfigurationState = {
    sidebarWidth: 300,
    sidebarOpen: true,
    sidebarPinned: true,
    displayMenuOpen: false,
    shareMenuOpen: false,
    printMenuOpen: false,
    messagingOpen: false,
    printSupplementary: false,
    promptLandscape: false,
    promptAll: false,
    wordAsText: false,
    printConfig: {
        schoolFont: {
            name: "Verdana",
            size: 14,
            weight: 500,
            decoration: "normal",
        },
        tableHeadFont: {
            name: "Verdana",
            size: 12,
            weight: 700,
            decoration: "normal",
        },
        tableFont: {
            name: "Verdana",
            size: 9,
            weight: 500,
            decoration: "normal",
        },
        margin: {
            top: 10,
            left: 10,
            bottom: 10,
            right: 10,
        },
        addColumns: false,
        noColumns: 1,
        colWidth: 20,
        pageOrientation: 0,
        separatePages: false,
        preview: true,
        initialZoom:
            window.innerWidth <= 400
                ? 35
                : window.innerWidth <= 500
                ? 40
                : window.innerWidth <= 1024
                ? 80
                : 100,
        printZoom:
            window.innerWidth <= 400
                ? 35
                : window.innerWidth <= 500
                ? 40
                : window.innerWidth <= 1024
                ? 80
                : 100,
    },
    displayMode: "auto",
    disableDOC: false,
    disableXLS: false,
    disablePDF: false,
    disableShare: false,
    reportName: "",
    reportsSidebar: true,
    pageLoading: false,
    pageLoadingOverlay: true,
    expandedMenuItems: [],
};

// Create a slice to hold all our reducers
const uiConfigurationSlice = createSlice({
    name: "uiConfiguration",
    initialState: initialState,
    reducers: {
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload;
        },
        setSidebarWidth: (state, action: PayloadAction<number>) => {
            state.sidebarWidth = action.payload;
        },
        setSidebarPinned: (state, action: PayloadAction<boolean>) => {
            state.sidebarPinned = action.payload;
        },
        setDisplayMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.displayMenuOpen = action.payload;
        },
        setShareMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.shareMenuOpen = action.payload;
        },
        setPrintMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.printMenuOpen = action.payload;
        },
        setMessagingOpen: (state, action: PayloadAction<boolean>) => {
            state.messagingOpen = action.payload;
        },
        setPromptLandscape: (state, action: PayloadAction<boolean>) => {
            state.promptLandscape = action.payload;
        },
        setPromptAll: (state, action: PayloadAction<boolean>) => {
            state.promptAll = action.payload;
        },
        setWordAsText: (state, action: PayloadAction<boolean>) => {
            state.wordAsText = action.payload;
        },
        setPrintSupplementary: (state, action: PayloadAction<boolean>) => {
            state.printSupplementary = action.payload;
        },
        setCustomPrintMethod: (
            state,
            action: PayloadAction<
                (target: PrintTarget, method: "Table" | "Text") => Promise<any>
            >,
        ) => {
            state.customPrintMethod = action.payload;
        },
        setCustomPrintTitle: (state, action: PayloadAction<string>) => {
            state.customPrintTitle = action.payload;
        },
        setCustomPrintName: (state, action: PayloadAction<string>) => {
            state.customPrintName = action.payload;
        },
        setCustomPrintElement: (state, action: PayloadAction<string>) => {
            state.customPrintElement = action.payload;
        },
        setSchoolFont: (state, action: PayloadAction<Font>) => {
            state.printConfig.schoolFont = action.payload;
        },
        setTableHeadFont: (state, action: PayloadAction<Font>) => {
            state.printConfig.tableHeadFont = action.payload;
        },
        setTableFont: (state, action: PayloadAction<Font>) => {
            state.printConfig.tableFont = action.payload;
        },
        setMargin: (state, action: PayloadAction<Margin>) => {
            state.printConfig.margin = action.payload;
        },
        setAddColumns: (state, action: PayloadAction<boolean>) => {
            state.printConfig.addColumns = action.payload;
        },
        setNoColumns: (state, action: PayloadAction<number>) => {
            state.printConfig.noColumns = action.payload;
        },
        setColWidth: (state, action: PayloadAction<number>) => {
            state.printConfig.colWidth = action.payload;
        },
        setSeparatePages: (state, action: PayloadAction<boolean>) => {
            state.printConfig.separatePages = action.payload;
        },
        setPageOrientation: (state, action: PayloadAction<number>) => {
            state.printConfig.pageOrientation = action.payload;
        },
        setPrintDate: (state, action: PayloadAction) => {
            state.printConfig.printDate = new Date().toISOString();
        },
        setPreview: (state, action: PayloadAction<boolean>) => {
            state.printConfig.preview = action.payload;
        },
        setPrintZoom: (state, action: PayloadAction<number>) => {
            state.printConfig.printZoom = action.payload;
        },
        resetConfiguration: (state, action: PayloadAction) => {
            state.promptLandscape = false;
            state.printSupplementary = false;
            state.wordAsText = false;
            state.promptAll = false;
            state.customPrintMethod = undefined;
            state.customPrintTitle = undefined;
            state.customPrintName = undefined;
            state.customPrintElement = undefined;
            state.disableDOC = false;
            state.disableXLS = false;
            state.disablePDF = false;
            state.disableShare = false;
            state.reportName = "";
            state.reportsSidebar = true;
            delete state.printConfig.printDate;
        },
        setCurrentFile: (state, action: PayloadAction<number>) => {
            state.currentFile = action.payload;
        },
        setDisplayMode: (state, action: PayloadAction<DisplayMode>) => {
            state.displayMode = action.payload;
        },
        setDisableDoc: (state, action: PayloadAction<boolean>) => {
            state.disableDOC = action.payload;
        },
        setDisableXLS: (state, action: PayloadAction<boolean>) => {
            state.disableXLS = action.payload;
        },
        setDisablePDF: (state, action: PayloadAction<boolean>) => {
            state.disablePDF = action.payload;
        },
        setDisableShare: (state, action: PayloadAction<boolean>) => {
            state.disableShare = action.payload;
        },
        setReportName: (state, action: PayloadAction<string>) => {
            state.reportName = action.payload;
        },
        setReportsSidebar: (state, action: PayloadAction<boolean>) => {
            state.reportsSidebar = action.payload;
        },
        setPageLoading: (state, action: PayloadAction<boolean>) => {
            state.pageLoading = action.payload;
        },
        setPageLoadingOverlay: (state, action: PayloadAction<boolean>) => {
            state.pageLoadingOverlay = action.payload;
        },
        addExpandedMenuItem: (state, action: PayloadAction<string>) => {
            if (!state.expandedMenuItems.includes(action.payload))
                state.expandedMenuItems.push(action.payload);
        },
        remExpandedMenuItem: (state, action: PayloadAction<string>) => {
            if (state.expandedMenuItems.includes(action.payload))
                state.expandedMenuItems = state.expandedMenuItems.filter(
                    item => item !== action.payload,
                );
        },
        setExpandedMenuItems: (state, action: PayloadAction<string[]>) => {
            state.expandedMenuItems = action.payload;
        },
        clearExpandedMenuItems: (state, action: PayloadAction) => {
            state.expandedMenuItems = [];
        },
    },
    extraReducers(builder) {
        builder.addCase(REHYDRATE, (_, rawData: any) => {
            if (rawData?.key !== "tts") return undefined;

            return {
                sidebarWidth:
                    rawData?.payload?.ui?.sidebarWidth ??
                    initialState.sidebarWidth,
                sidebarOpen:
                    rawData?.payload?.ui?.sidebarOpen ??
                    initialState.sidebarOpen,
                sidebarPinned:
                    rawData?.payload?.ui?.sidebarPinned ??
                    initialState.sidebarPinned,
                displayMenuOpen: initialState.displayMenuOpen,
                shareMenuOpen: initialState.shareMenuOpen,
                printMenuOpen: initialState.printMenuOpen,
                messagingOpen: initialState.messagingOpen,
                printSupplementary: initialState.printSupplementary,
                promptLandscape: initialState.promptLandscape,
                promptAll: initialState.promptAll,
                wordAsText: initialState.wordAsText,
                printConfig:
                    rawData?.payload?.ui?.printConfig ??
                    initialState.printConfig,
                displayMode:
                    rawData?.payload?.ui?.displayMode ??
                    initialState.displayMode,
                disableDOC: initialState.disableDOC,
                disableXLS: initialState.disableXLS,
                disablePDF: initialState.disablePDF,
                disableShare: initialState.disableShare,
                reportName: initialState.reportName,
                reportsSidebar: initialState.reportsSidebar,
                pageLoading: initialState.pageLoading,
                pageLoadingOverlay: initialState.pageLoadingOverlay,
                expandedMenuItems:
                    rawData?.payload?.ui?.expandedMenuItems ??
                    initialState.expandedMenuItems,
            };
        });
    },
});

export const {
    setSidebarOpen,
    setSidebarWidth,
    setSidebarPinned,
    setDisplayMenuOpen,
    setShareMenuOpen,
    setPrintMenuOpen,
    setMessagingOpen,
    setSchoolFont,
    setTableHeadFont,
    setTableFont,
    setMargin,
    setAddColumns,
    setNoColumns,
    setColWidth,
    setSeparatePages,
    setPageOrientation,
    setPromptLandscape,
    setPromptAll,
    setWordAsText,
    setPrintSupplementary,
    setCustomPrintMethod,
    setCustomPrintTitle,
    setCustomPrintName,
    setCustomPrintElement,
    setPrintDate,
    setPreview,
    setPrintZoom,
    resetConfiguration,
    setCurrentFile,
    setDisplayMode,
    setDisableDoc,
    setDisableXLS,
    setDisablePDF,
    setReportName,
    setDisableShare,
    setReportsSidebar,
    setPageLoading,
    setPageLoadingOverlay,
    addExpandedMenuItem,
    remExpandedMenuItem,
    setExpandedMenuItems,
    clearExpandedMenuItems,
} = uiConfigurationSlice.actions;

export function getDisplayMode() {
    const { ui } = store.getState().db;

    if (ui.displayMode === "auto") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light";
    }

    return ui.displayMode;
}

// We have to export the reducer as this is added to the state store
export default uiConfigurationSlice.reducer;
